/**
 * Push Notification Service for SenseiSuite Mobile App
 * Handles FCM registration, permissions, and notification handling
 */

import { Capacitor } from '@capacitor/core';
import { PushNotifications } from '@capacitor/push-notifications';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

class PushNotificationService {
  constructor() {
    this.initialized = false;
    this.fcmToken = null;
    this.listeners = [];
  }

  /**
   * Check if push notifications are supported on this platform
   */
  isSupported() {
    return Capacitor.isNativePlatform();
  }

  /**
   * Initialize push notifications
   * Call this on app startup after user login
   */
  async initialize() {
    if (!this.isSupported()) {
      console.log('Push notifications not supported on web');
      return { success: false, reason: 'not_supported' };
    }

    if (this.initialized) {
      console.log('Push notifications already initialized');
      return { success: true, token: this.fcmToken };
    }

    try {
      // Request permission
      const permStatus = await PushNotifications.requestPermissions();
      
      if (permStatus.receive !== 'granted') {
        console.log('Push notification permission denied');
        return { success: false, reason: 'permission_denied' };
      }

      // Set up listeners before registering
      this.setupListeners();

      // Register for push notifications
      await PushNotifications.register();

      this.initialized = true;
      console.log('Push notifications initialized successfully');
      
      return { success: true };
    } catch (error) {
      console.error('Failed to initialize push notifications:', error);
      return { success: false, reason: 'initialization_failed', error: error.message };
    }
  }

  /**
   * Set up event listeners for push notifications
   */
  setupListeners() {
    // Registration success - receive FCM token
    PushNotifications.addListener('registration', async (token) => {
      console.log('FCM Token received:', token.value);
      this.fcmToken = token.value;
      
      // Save token to backend
      await this.saveTokenToBackend(token.value);
      
      // Notify any registered callbacks
      this.listeners.forEach(listener => {
        if (listener.event === 'registration') {
          listener.callback(token.value);
        }
      });
    });

    // Registration error
    PushNotifications.addListener('registrationError', (error) => {
      console.error('Push registration error:', error);
      this.listeners.forEach(listener => {
        if (listener.event === 'registrationError') {
          listener.callback(error);
        }
      });
    });

    // Notification received while app is in foreground
    PushNotifications.addListener('pushNotificationReceived', (notification) => {
      console.log('Push notification received:', notification);
      this.listeners.forEach(listener => {
        if (listener.event === 'pushNotificationReceived') {
          listener.callback(notification);
        }
      });
    });

    // User tapped on notification
    PushNotifications.addListener('pushNotificationActionPerformed', (action) => {
      console.log('Push notification action performed:', action);
      this.listeners.forEach(listener => {
        if (listener.event === 'pushNotificationActionPerformed') {
          listener.callback(action);
        }
      });
    });
  }

  /**
   * Add event listener for push notification events
   * @param {string} event - Event name: 'registration', 'registrationError', 'pushNotificationReceived', 'pushNotificationActionPerformed'
   * @param {function} callback - Callback function
   */
  addListener(event, callback) {
    this.listeners.push({ event, callback });
  }

  /**
   * Remove event listener
   * @param {string} event - Event name
   * @param {function} callback - Callback function to remove
   */
  removeListener(event, callback) {
    this.listeners = this.listeners.filter(
      listener => !(listener.event === event && listener.callback === callback)
    );
  }

  /**
   * Save FCM token to backend for this user
   * @param {string} token - FCM token
   */
  async saveTokenToBackend(token) {
    try {
      const authToken = localStorage.getItem('token');
      if (!authToken) {
        console.log('No auth token, skipping FCM token save');
        return;
      }

      const response = await axios.post(
        `${API}/fcm-token`,
        { fcm_token: token },
        {
          headers: {
            'Authorization': `Bearer ${authToken}`,
            'Content-Type': 'application/json'
          }
        }
      );

      console.log('FCM token saved to backend:', response.data);
      return response.data;
    } catch (error) {
      console.error('Failed to save FCM token to backend:', error);
    }
  }

  /**
   * Remove FCM token from backend (call on logout)
   */
  async removeTokenFromBackend() {
    try {
      const authToken = localStorage.getItem('token');
      if (!authToken || !this.fcmToken) {
        return;
      }

      await axios.delete(
        `${API}/fcm-token`,
        {
          data: { fcm_token: this.fcmToken },
          headers: {
            'Authorization': `Bearer ${authToken}`,
            'Content-Type': 'application/json'
          }
        }
      );

      console.log('FCM token removed from backend');
    } catch (error) {
      console.error('Failed to remove FCM token from backend:', error);
    }
  }

  /**
   * Get current FCM token
   */
  getToken() {
    return this.fcmToken;
  }

  /**
   * Check notification permission status
   */
  async checkPermissions() {
    if (!this.isSupported()) {
      return { receive: 'denied' };
    }
    return await PushNotifications.checkPermissions();
  }
}

// Export singleton instance
const pushNotificationService = new PushNotificationService();
export default pushNotificationService;
